/**
 * The languages Cuadra speaks. Adding one = a folder in locales/ (copy es/ and translate)
 * plus a line here. Spanish is the source: any key missing in another language shows in Spanish.
 */
export const LANGUAGES = [
  { code: 'es', name: 'Español', htmlLang: 'es' },
  { code: 'en', name: 'English', htmlLang: 'en' },
  { code: 'lv', name: 'Latviešu', htmlLang: 'lv' },
  { code: 'pt', name: 'Português', htmlLang: 'pt-BR' },
] as const;

export type Language = (typeof LANGUAGES)[number]['code'];
export const SOURCE_LANGUAGE: Language = 'es';
/** Same key on the landing, the app and Finanzas: they share the origin, so the choice follows the person. */
export const STORAGE_KEY = 'cuadra-lang';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && LANGUAGES.some((language) => language.code === value);
}

/** "pt-BR" → "pt", "en_GB" → "en"; null when Cuadra doesn't speak it. */
export function toLanguage(tag: string | null | undefined): Language | null {
  const base = (tag ?? '').toLowerCase().split(/[-_]/)[0];
  return isLanguage(base) ? base : null;
}

// Where the device is, read from its time zone: no IP lookup leaves the browser.
const PORTUGUESE_ZONES = /^(America\/(Sao_Paulo|Fortaleza|Recife|Bahia|Maceio|Belem|Manaus|Cuiaba|Campo_Grande|Porto_Velho|Boa_Vista|Rio_Branco|Araguaina|Santarem|Noronha|Eirunepe)|Europe\/Lisbon|Atlantic\/(Azores|Madeira))$/;
const SPANISH_ZONES = /^(America\/(Argentina\/.*|Buenos_Aires|Cordoba|Mendoza|Montevideo|Santiago|Punta_Arenas|Asuncion|La_Paz|Lima|Bogota|Caracas|Guayaquil|Mexico_City|Monterrey|Merida|Cancun|Chihuahua|Hermosillo|Mazatlan|Tijuana|Matamoros|Bahia_Banderas|Ojinaga|Panama|Costa_Rica|Guatemala|El_Salvador|Tegucigalpa|Managua|Havana|Santo_Domingo|Puerto_Rico)|Europe\/Madrid|Africa\/Ceuta|Atlantic\/Canary|Pacific\/(Easter|Galapagos))$/;

export function languageFromTimeZone(zone: string | undefined): Language | null {
  if (!zone) return null;
  if (zone === 'Europe/Riga') return 'lv';
  if (PORTUGUESE_ZONES.test(zone)) return 'pt';
  if (SPANISH_ZONES.test(zone)) return 'es';
  return null;
}

/** First visit: where the device is, then what the browser asks for, then English. */
export function detectLanguage(): Language {
  let zone: string | undefined;
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    zone = undefined;
  }
  const byPlace = languageFromTimeZone(zone);
  if (byPlace) return byPlace;
  const asked = typeof navigator === 'undefined' ? [] : navigator.languages ?? [navigator.language];
  for (const tag of asked) {
    const language = toLanguage(tag);
    if (language) return language;
  }
  return 'en';
}

export function storedLanguage(): Language | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

export function storeLanguage(language: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Private mode: the choice lasts this visit only.
  }
}

/** ?lang=en in a link wins once and is remembered, so a shared link opens in its language. */
export function languageFromUrl(): Language | null {
  try {
    return toLanguage(new URLSearchParams(window.location.search).get('lang'));
  } catch {
    return null;
  }
}

export function preferredLanguage(): Language {
  const fromUrl = languageFromUrl();
  if (fromUrl) storeLanguage(fromUrl);
  return fromUrl ?? storedLanguage() ?? detectLanguage();
}
